import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Public enquiry-capture form on a business mini-site (shops with the `enquiry` capability). Posts
 * to the city-scoped, per-business-throttled capture endpoint; the business is named by id in the
 * path, the city by the signed city-token — never by the form. A phone or email is required so the
 * shop can actually reply.
 *
 * F4: another block (the ISP coverage check, L2-004; a membership plan's "Join" CTA, L2-006) can
 * set `AppContext.enquiryPrefill` right before it scrolls here, to pre-fill the message (and,
 * for the coverage check, the phone if it captured one first). Consumed once — as soon as it
 * shows up — then cleared, so a prefill from a click three minutes ago never silently reappears
 * on a later, unrelated visit to this same section.
 *
 * **RE-UAT-007: this section is normally already mounted when the prefill arrives.** It is part
 * of the storefront's always-rendered section list, so by the time a visitor clicks "Join" (or
 * runs the coverage check) `enquiryPrefill` is still `null` at THIS component's mount — reading
 * it only in `useState`'s initializer (mount-only) silently dropped every real prefill. The
 * effect below reacts to `enquiryPrefill` itself, seeding the form whenever a prefill shows up,
 * mount or not. */
export function EnquirySection({ business }) {
  const { t, enquiryPrefill, setEnquiryPrefill } = useApp();
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    message: "",
  });

  useEffect(() => {
    if (!enquiryPrefill) return;
    setForm((prev) => ({
      ...prev,
      contact_phone: enquiryPrefill.contact_phone || prev.contact_phone,
      message: enquiryPrefill.message || prev.message,
    }));
    setEnquiryPrefill(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryPrefill]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend = form.contact_phone.trim() !== "" || form.contact_email.trim() !== "";

  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      await api.post(`/city/businesses/${business.id}/enquiries`, {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
        message: form.message.trim(),
      });
      setForm({ contact_name: "", contact_phone: "", contact_email: "", message: "" });
      setSent(true);
      toast.success(t("enquiry.sent"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        className="rounded-2xl border border-border p-6 text-center"
        data-testid="enquiry-sent"
      >
        <p className="text-sm text-muted-foreground">{t("enquiry.sentBody")}</p>
        <Button
          variant="outline"
          className="mt-4 rounded-full"
          data-testid="enquiry-another"
          onClick={() => setSent(false)}
        >
          {t("enquiry.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border p-5" data-testid="enquiry-section">
      <p className="text-sm text-muted-foreground">{t("enquiry.intro")}</p>
      <div className="space-y-1.5">
        <Label htmlFor="enq-name">{t("enquiry.name")}</Label>
        <Input
          id="enq-name"
          data-testid="enquiry-name"
          value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="enq-phone">{t("enquiry.phone")}</Label>
          <Input
            id="enq-phone"
            data-testid="enquiry-phone"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="enq-email">{t("enquiry.email")}</Label>
          <Input
            id="enq-email"
            type="email"
            data-testid="enquiry-email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="enq-message">{t("enquiry.message")}</Label>
        <Textarea
          id="enq-message"
          rows={4}
          data-testid="enquiry-message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
      </div>
      <Button
        className="rounded-full"
        disabled={busy || !canSend}
        data-testid="enquiry-submit"
        onClick={submit}
      >
        {t("enquiry.submit")}
      </Button>
    </div>
  );
}
